import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  AlertTitle,
  Collapse,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { logger } from '../services/logger';
import * as Sentry from '@sentry/react';

const log = logger.createScope('ErrorBoundary');

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
  showDetails: boolean;
}

const ErrorContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.mode === 'dark' 
    ? theme.palette.grey[900] 
    : theme.palette.grey[50],
  padding: theme.spacing(2),
}));

const ErrorCard = styled(Card)(({ theme }) => ({
  maxWidth: 600,
  width: '100%',
  boxShadow: theme.shadows[8],
  backgroundColor: theme.palette.background.paper,
}));

class ErrorBoundary extends Component<Props, State> {
  private retryCount = 0;
  private maxRetries = 3;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Generate unique error ID for tracking
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    return {
      hasError: true,
      error,
      errorId,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    log.error('Error Boundary Caught Error', error, { 
      errorName: error.name, 
      errorMessage: error.message,
      componentStack: errorInfo.componentStack 
    });

    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error to external service (if needed)
    this.logErrorToService(error, errorInfo);
  }

  private logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    // Log to logger service
    log.error('Error logged to service', new Error(errorData.message), { errorId: errorData.errorId, url: errorData.url });
    
    // Send to Sentry error tracking service
    try {
      Sentry.captureException(error, {
        extra: errorData,
        tags: {
          errorId: this.state.errorId,
          errorCategory: this.getErrorCategory(error),
          errorSeverity: this.getErrorSeverity(error),
        },
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
      });
    } catch (sentryError) {
      // If Sentry fails, log it but don't break the error boundary
      log.warn('Failed to send error to Sentry', { error: sentryError });
    }
  };

  private handleRetry = () => {
    this.retryCount++;
    
    if (this.retryCount <= this.maxRetries) {
      log.debug('Retrying after error', { retryCount: this.retryCount, maxRetries: this.maxRetries, errorId: this.state.errorId });
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        errorId: '',
        showDetails: false,
      });
    } else {
      log.warn('Max retries exceeded', { retryCount: this.retryCount, maxRetries: this.maxRetries, errorId: this.state.errorId });
      // Show different message or redirect
    }
  };

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private toggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails,
    }));
  };

  private getErrorSeverity = (error: Error): 'error' | 'warning' => {
    // Determine error severity based on error type/message
    const criticalErrors = [
      'ChunkLoadError',
      'TypeError: Cannot read',
      'ReferenceError',
      'SyntaxError'
    ];
    
    const isCritical = criticalErrors.some(criticalError => 
      error.message.includes(criticalError) || error.name.includes(criticalError)
    );
    
    return isCritical ? 'error' : 'warning';
  };

  private getErrorCategory = (error: Error): string => {
    if (error.message.includes('ChunkLoadError')) return 'Network/Loading';
    if (error.message.includes('TypeError')) return 'JavaScript';
    if (error.message.includes('Firebase')) return 'Database';
    if (error.message.includes('fetch')) return 'API';
    return 'Application';
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorInfo: _errorInfo, errorId, showDetails } = this.state;
      const severity = error ? this.getErrorSeverity(error) : 'error';
      const category = error ? this.getErrorCategory(error) : 'Unknown';
      const canRetry = this.retryCount < this.maxRetries;

      return (
        <ErrorContainer>
          <ErrorCard>
            <CardContent sx={{ p: 4 }}>
              {/* Error Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <ErrorIcon 
                  sx={{ 
                    fontSize: 48, 
                    color: severity === 'error' ? 'error.main' : 'warning.main',
                    mr: 2 
                  }} 
                />
                <Box>
                  <Typography variant="h5" gutterBottom>
                    Oops! Something went wrong
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Chip 
                      label={category} 
                      size="small" 
                      color={severity} 
                      variant="outlined" 
                    />
                    <Chip 
                      label={errorId} 
                      size="small" 
                      variant="outlined" 
                    />
                  </Stack>
                </Box>
              </Box>

              {/* Error Message */}
              <Alert severity={severity} sx={{ mb: 3 }}>
                <AlertTitle>Error Details</AlertTitle>
                <Typography variant="body2">
                  {error?.message || 'An unexpected error occurred while loading this page.'}
                </Typography>
              </Alert>

              {/* User-Friendly Message */}
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Don't worry! This is likely a temporary issue. Here are some things you can try:
              </Typography>

              {/* Action Buttons */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
                {canRetry && (
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleRetry}
                    color="primary"
                  >
                    Try Again ({this.maxRetries - this.retryCount} left)
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleRefresh}
                >
                  Refresh Page
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<HomeIcon />}
                  onClick={this.handleGoHome}
                >
                  Go Home
                </Button>
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* Technical Details Toggle */}
              <Box>
                <Button
                  variant="text"
                  startIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={this.toggleDetails}
                  size="small"
                  sx={{ mb: 1 }}
                >
                  {showDetails ? 'Hide' : 'Show'} Technical Details
                </Button>
                
                <Collapse in={showDetails}>
                  <Alert severity="info" variant="outlined">
                    <AlertTitle>Technical Information</AlertTitle>
                    {error && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" display="block" gutterBottom>
                          <strong>Error:</strong> {error.message}
                        </Typography>
                        <Typography variant="caption" display="block" gutterBottom>
                          <strong>Type:</strong> {error.name}
                        </Typography>
                        {error.stack && (
                          <Typography variant="caption" display="block" sx={{ 
                            fontFamily: 'monospace', 
                            mt: 1,
                            maxHeight: 200,
                            overflow: 'auto',
                            backgroundColor: 'grey.100',
                            p: 1,
                            borderRadius: 1,
                          }}>
                            {error.stack}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Alert>
                </Collapse>
              </Box>

              {/* Contact Support */}
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  If the problem persists, please contact support with error ID: {errorId}
                </Typography>
              </Box>
            </CardContent>
          </ErrorCard>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 